import { syncPropertyNames } from '@theme/roots-addon-properties';

/**
 * Blouse fit module (snippets/roots-blouse-fit.liquid).
 *
 * Switches between "Standard size" (size chips) and "Custom measurements"
 * (measurement inputs), enabling and requiring only the fields for the active
 * mode so the browser validates exactly what the shopper can see. The radios
 * have module-unique names (two modules can share one product form), so the
 * "Blouse fit" and "Blouse size" properties live on hidden inputs synced here. While an
 * ancestor is inert (a closed add-on panel) every field stays disabled; the
 * add-on component calls `sync()` after it opens or closes.
 */
class RootsBlouseFit extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  /** @returns {'standard' | 'custom'} */
  get mode() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-fit-mode]:checked'));
    return checked?.dataset.fitMode === 'custom' ? 'custom' : 'standard';
  }

  /** True unless a closed add-on panel (or anything else inert) contains this module. */
  get active() {
    return !this.closest('[inert]');
  }

  sync() {
    const active = this.active;
    const mode = this.mode;
    const standard = mode === 'standard';

    this.dataset.mode = mode;

    for (const panel of this.querySelectorAll('[data-fit-panel]')) {
      panel.toggleAttribute('hidden', panel.getAttribute('data-fit-panel') !== mode);
    }

    for (const input of this.#inputs('[data-fit-mode]')) {
      input.disabled = !active;
    }

    for (const input of this.#inputs('[data-fit-standard-field]')) {
      input.disabled = !active || !standard;
      input.required = active && standard;
    }

    for (const input of this.#inputs('[data-fit-custom-field]')) {
      input.disabled = !active || standard;
      input.required = active && !standard;
    }

    const modeProperty = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-fit-property]'));
    if (modeProperty) {
      modeProperty.disabled = !active;
      modeProperty.value = this.querySelector('[data-fit-mode]:checked')?.getAttribute('value') ?? 'Standard size';
    }

    this.#syncSize();
  }

  /** Copies the chosen chip into the "Blouse size" property and highlights its chart row. */
  #syncSize() {
    const selected = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-fit-standard-field]:checked'));
    const sizeProperty = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-fit-size-property]'));
    if (sizeProperty) {
      sizeProperty.value = selected?.value ?? '';
      sizeProperty.disabled = !this.active || this.mode !== 'standard' || !selected;
    }
    for (const row of this.querySelectorAll('[data-chart-size]')) {
      row.classList.toggle('is-selected', Boolean(selected) && row.getAttribute('data-chart-size') === selected?.value);
    }

    // Last step of every path through this module (`sync` ends here too), so
    // the property names always match what is enabled right now.
    syncPropertyNames(this);
  }

  /**
   * @param {string} selector
   * @returns {NodeListOf<HTMLInputElement>}
   */
  #inputs(selector) {
    return /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll(selector));
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if ('fitMode' in target.dataset) this.sync();
    else if ('fitStandardField' in target.dataset) this.#syncSize();
  };

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // "How to measure" opens the guide as a modal dialog.
    const opener = /** @type {HTMLElement | null} */ (event.target.closest('[data-guide-open]'));
    if (opener) {
      const id = opener.dataset.guideOpen;
      const dialog = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
      return;
    }

    // Close on the × button or a click on the backdrop (the dialog itself).
    const dialog = event.target.closest('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      if (event.target.closest('[data-guide-close]') || event.target === dialog) dialog.close();
      return;
    }

    // "Size guide" toggles the chart inline.
    const button = /** @type {HTMLElement | null} */ (event.target.closest('[data-guide-toggle]'));
    if (!button) return;

    const targetId = button.getAttribute('aria-controls');
    const target = targetId ? this.querySelector(`#${CSS.escape(targetId)}`) : null;
    if (!target) return;

    const open = target.hasAttribute('hidden');
    target.toggleAttribute('hidden', !open);
    button.setAttribute('aria-expanded', String(open));
    const label = open ? button.dataset.labelClose : button.dataset.labelOpen;
    if (label) button.textContent = label;
  };
}

if (!customElements.get('roots-blouse-fit')) {
  customElements.define('roots-blouse-fit', RootsBlouseFit);
}
